import {
  ApiError,
  type CameraMedia,
  type MachineCamera,
  queryKeys,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { CameraCaptureDialog } from './camera-capture-dialog.js'
import { CameraMediaDialog } from './camera-media-dialog.js'
import { CameraMediaGrid } from './camera-media-grid.js'

interface CameraDraft {
  name: string
  snapshotUrl: string
  streamUrl: string
}

const EMPTY_DRAFT: CameraDraft = { name: '', snapshotUrl: '', streamUrl: '' }

/** A camera's address can carry its login; the list shows only up to the path. */
function redact(url: string): string {
  return url.replace(/\/\/[^@/]*@/, '//').replace(/\?.*$/, '?…')
}

function CamerasCard({ machineId, isCabinet }: { machineId: string; isCabinet: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  // `null` = closed, `'new'` = adding, otherwise the camera being edited.
  const [editing, setEditing] = useState<MachineCamera | 'new' | null>(null)
  const [draft, setDraft] = useState<CameraDraft>(EMPTY_DRAFT)

  const { data: cameras, isLoading } = useQuery({
    queryKey: queryKeys.cameras.machineCameras(machineId),
    queryFn: () => api.get<MachineCamera[]>(`/vending/machines/${machineId}/cameras`),
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.cameras.machineCameras(machineId) })
  const onError = (err: unknown) =>
    toast.error('Could not save the camera', err instanceof ApiError ? err.message : undefined)

  const save = useMutation({
    mutationFn: (input: { camera: MachineCamera | null; draft: CameraDraft }) => {
      const body = {
        name: input.draft.name.trim(),
        snapshotUrl: input.draft.snapshotUrl.trim(),
        streamUrl: input.draft.streamUrl.trim() || null,
      }
      return input.camera
        ? api.patch<MachineCamera>(`/vending/machines/${machineId}/cameras/${input.camera.id}`, {
            ...body,
            enabled: input.camera.enabled,
          })
        : api.post<MachineCamera>(`/vending/machines/${machineId}/cameras`, body)
    },
    onSuccess: () => {
      void invalidate()
      setEditing(null)
      toast.success('Camera saved')
    },
    onError,
  })
  const toggle = useMutation({
    mutationFn: (camera: MachineCamera) =>
      api.patch<MachineCamera>(`/vending/machines/${machineId}/cameras/${camera.id}`, {
        enabled: !camera.enabled,
      }),
    onSuccess: invalidate,
    onError,
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/vending/machines/${machineId}/cameras/${id}`),
    onSuccess: invalidate,
    onError,
  })

  const open = (camera: MachineCamera | 'new') => {
    setDraft(
      camera === 'new'
        ? EMPTY_DRAFT
        : { name: camera.name, snapshotUrl: camera.snapshotUrl, streamUrl: camera.streamUrl ?? '' },
    )
    setEditing(camera)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Camera className="size-4" /> Cameras
            </CardTitle>
            <CardDescription>
              {isCabinet
                ? 'IP cameras on the same network as the tablet. At each step of a pickup the tablet grabs a still from every enabled camera and attaches it to the order; it also fetches the snapshots and clips you ask for here.'
                : 'IP cameras on the same network as this machine’s controller. When you ask for a snapshot or clip, the controller fetches it from the camera and uploads it here.'}
            </CardDescription>
          </div>
          <Button size="sm" variant="secondary" onClick={() => open('new')}>
            <Plus /> Add camera
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? null : cameras?.length === 0 ? (
          <p className="text-sm text-foreground-muted">
            No cameras yet. Add one to start taking snapshots and clips.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {cameras?.map((camera) => (
              <li key={camera.id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    {camera.name}
                    <Badge tone={camera.streamUrl ? 'success' : 'neutral'}>
                      {camera.streamUrl ? 'Snapshots and clips' : 'Snapshots only'}
                    </Badge>
                  </p>
                  <p className="truncate font-identifier text-2xs text-foreground-subtle">
                    {redact(camera.snapshotUrl)}
                  </p>
                </div>
                <Switch
                  checked={camera.enabled}
                  aria-label={`${camera.enabled ? 'Disable' : 'Enable'} ${camera.name}`}
                  onCheckedChange={() => toggle.mutate(camera)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Edit ${camera.name}`}
                  onClick={() => open(camera)}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${camera.name}`}
                  onClick={() => remove.mutate(camera.id)}
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(isOpen) => !isOpen && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate({ camera: editing === 'new' ? null : editing, draft })
            }}
          >
            <DialogHeader>
              <DialogTitle>{editing === 'new' ? 'Add a camera' : 'Edit camera'}</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <Field label="Name" required>
                {(props) => (
                  <Input
                    {...props}
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Front of machine"
                  />
                )}
              </Field>
              <Field
                label="Snapshot address"
                required
                hint="For a Reolink camera: http://<camera IP>/cgi-bin/api.cgi?cmd=Snap&channel=0&user=<user>&password=<password>. Use a view-only user created on the camera."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={draft.snapshotUrl}
                    onChange={(e) => setDraft({ ...draft, snapshotUrl: e.target.value })}
                    className="font-identifier"
                    placeholder="http://192.168.1.50/cgi-bin/api.cgi?cmd=Snap&channel=0&…"
                  />
                )}
              </Field>
              <Field
                label="Video stream address (for clips)"
                hint="For a Reolink camera: rtsp://<user>:<password>@<camera IP>:554/h264Preview_01_main. RTSP is off by default on Reolink cameras: enable it first under Port Settings in the Reolink app or client. Leave empty if you only need snapshots."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={draft.streamUrl}
                    onChange={(e) => setDraft({ ...draft, streamUrl: e.target.value })}
                    className="font-identifier"
                    placeholder="rtsp://viewer:…@192.168.1.50:554/h264Preview_01_main"
                  />
                )}
              </Field>
              <p className="text-xs text-foreground-subtle">
                These addresses can contain the camera’s login, so they are never shown in full
                again or written to the audit log.
              </p>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={save.isPending}
                disabled={!draft.name.trim() || !draft.snapshotUrl.trim()}
              >
                Save camera
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/** The "Cameras" tab of a vending machine's detail page. */
export function MachineCamerasTab({ machine }: { machine: VendingMachine }) {
  const { can } = useAuth()
  const [capturing, setCapturing] = useState(false)
  const [viewing, setViewing] = useState<CameraMedia | null>(null)
  // A glass cabinet has no Pi: its tablet fetches the stills and clips.
  const isCabinet = machine.kind === 'cabinet'
  const hasCaptureDevice = isCabinet ? machine.tabletDeviceId !== null : machine.piDeviceId !== null

  if (!can('kiosks:manage')) {
    return (
      <Alert tone="info">
        Viewing and taking camera captures needs the Manage kiosks permission.
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      {!hasCaptureDevice ? (
        <Alert tone="warning">
          {isCabinet
            ? 'This machine has no checkout tablet paired yet, so it can’t take captures. Pair one on the Devices tab.'
            : 'This machine has no controller (Raspberry Pi) paired yet, so it can’t take captures. Pair one on the Devices tab.'}
        </Alert>
      ) : null}

      <CamerasCard machineId={machine.id} isCabinet={isCabinet} />

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle>Captures</CardTitle>
              <CardDescription>
                Kept for 90 days, unless attached to a support ticket. Open one to view it or attach
                it to a ticket.
              </CardDescription>
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={!hasCaptureDevice}
              onClick={() => setCapturing(true)}
            >
              <Camera /> Capture
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <CameraMediaGrid
            filters={{ machineId: machine.id }}
            onSelect={setViewing}
            showMachine={false}
          />
        </CardContent>
      </Card>

      <CameraCaptureDialog
        open={capturing}
        onOpenChange={setCapturing}
        machineId={machine.id}
        onCaptured={setViewing}
      />
      <CameraMediaDialog media={viewing} onClose={() => setViewing(null)} />
    </div>
  )
}
